<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentHeadcount.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentHeadcount.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-3">
            <ts-loading-banner :loading="loading">
              <div class="card border-0">
                <div
                  class="card-body"
                  style="background-color: #20252a; border-radius: 5px"
                >
                  <div class="text-gray-500">
                    <b>{{ $t('studentHeadcount.filterCriteria') }}</b>
                  </div>
                </div>
                <div class="widget-list rounded-bottom">
                  <!-- Multiple Branch Selection -->
                  <div class="row form-group mt-2">
                    <div class="col-md-12">
                      <label>{{ $t('studentHeadcount.branch') }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.branch_id"
                        mode="multiple"
                        :options="branches"
                        :show-search="true"
                        :allow-clear="true"
                        :placeholder="$t('studentHeadcount.allBranches')"
                        :filter-option="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                      >
                      </a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>

                  <!-- Cycle filter (Month and Year tick box) -->
                  <div class="row form-group mt-2">
                    <div class="col-md-12">
                      <label>{{ $t('studentHeadcount.cycle') }}</label>
                      <div class="d-flex align-items-center mt-1">
                        <a-checkbox
                          :checked="model.scoring_cycle === 'month'"
                          @change="onCycleChange('month')"
                          class="mr-4"
                        >
                          {{ $t('studentHeadcount.month') }}
                        </a-checkbox>
                        <a-checkbox
                          :checked="model.scoring_cycle === 'yearly'"
                          @change="onCycleChange('yearly')"
                        >
                          {{ $t('studentHeadcount.year') }}
                        </a-checkbox>
                      </div>
                    </div>
                  </div>

                  <!-- Month Range filter (Monthly) -->
                  <div
                    class="row form-group"
                    v-if="model.scoring_cycle === 'month'"
                  >
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('studentHeadcount.monthRange')
                      }}</label>
                      <a-range-picker
                        style="width: 100%"
                        format="MM-YYYY"
                        :placeholder="[
                          $t('studentHeadcount.fromMonth'),
                          $t('studentHeadcount.toMonth')
                        ]"
                        :value="model.month_range"
                        :mode="pickerMode"
                        :open="isPickerOpen"
                        :allow-clear="true"
                        @openChange="onOpenChange"
                        @panelChange="onPanelChange"
                        @change="onRangeChange"
                      />
                      <div class="validate" v-if="errors.has('month_range')">
                        {{ errors.first('month_range') }}
                      </div>
                    </div>
                  </div>

                  <!-- Year Range filter (Yearly) -->
                  <div class="row form-group" v-else>
                    <div class="col-md-6 pr-1">
                      <label class="required">{{
                        $t('studentHeadcount.fromYear')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.from_year"
                        :options="yearOptions"
                        :placeholder="$t('studentHeadcount.selectFromYear')"
                        @change="onFromYearChange"
                      />
                      <div class="validate" v-if="errors.has('from_year')">
                        {{ errors.first('from_year') }}
                      </div>
                    </div>
                    <div class="col-md-6 pl-1">
                      <label class="required">{{
                        $t('studentHeadcount.toYear')
                      }}</label>
                      <a-select
                        style="width: 100%"
                        v-model="model.to_year"
                        :options="yearOptions"
                        :placeholder="$t('studentHeadcount.selectToYear')"
                      />
                      <div class="validate" v-if="errors.has('to_year')">
                        {{ errors.first('to_year') }}
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="row mt-3">
                    <div class="col-md-12 text-center mt-2">
                      <ts-button
                        class="btn-block"
                        outline
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                      >
                        <i class="fa fa-file-excel mr-1"></i>
                        {{ $t('studentHeadcount.exportExcel') }}
                      </ts-button>
                    </div>
                  </div>
                </div>
              </div>
            </ts-loading-banner>
          </div>
          <div class="col-md-9">
            <ts-preview-pdf v-model="waiting" :src="src" />
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import moment from 'moment'
import { trim } from 'lodash'

export default {
  name: 'StudentHeadcount',
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      src: '',
      errors: new Errors(),
      branches: [],
      pickerMode: ['month', 'month'],
      isPickerOpen: false,
      clickCount: 0,
      model: {
        branch_id: [],
        scoring_cycle: 'month',
        from_year: moment().year(),
        to_year: moment().year(),
        month_range: [moment(), moment()]
      }
    }
  },
  computed: {
    yearOptions() {
      const currentYear = moment().year()
      const years = []
      for (let y = currentYear + 2; y >= currentYear - 7; y--) {
        years.push({
          value: y,
          label: `${y}`
        })
      }
      return years
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('reportAcademic', [
      'getBranch',
      'studentHeadcount',
      'studentHeadcountExcel'
    ]),
    async fetchData() {
      this.loading = true
      try {
        const response = await this.getBranch()
        this.branches = response.data.map(el => ({
          value: el.branch_id,
          label: el.branch_name_en
        }))
        const defaultBranchId =
          this.$store.state.authUser &&
          this.$store.state.authUser.default_branch_id
        if (defaultBranchId) {
          this.model.branch_id = [defaultBranchId]
        } else if (this.branches.length > 0) {
          this.model.branch_id = [this.branches[0].value]
        } else {
          this.model.branch_id = []
        }
      } catch (error) {
        this.$toasted.error(error.message || 'Error loading branches')
      } finally {
        this.loading = false
      }
    },
    onCycleChange(cycle) {
      if (this.model.scoring_cycle !== cycle) {
        this.model.scoring_cycle = cycle
      } else {
        this.$forceUpdate()
      }
    },
    onFromYearChange(val) {
      if (this.model.to_year < val) {
        this.model.to_year = val
      }
    },
    onOpenChange(open) {
      this.isPickerOpen = open
      this.clickCount = 0
      if (open) {
        this.pickerMode = ['month', 'month']
      }
    },
    onPanelChange(value, mode) {
      this.model.month_range = value
      this.pickerMode = [
        mode[0] === 'date' ? 'month' : mode[0],
        mode[1] === 'date' ? 'month' : mode[1]
      ]
      this.clickCount++
      if (this.clickCount >= 2) {
        this.isPickerOpen = false
        this.clickCount = 0
      }
    },
    onRangeChange(value) {
      this.model.month_range = value
      this.clickCount = 0
    },
    getFormattedParams() {
      const isYearly = this.model.scoring_cycle === 'yearly'
      if (isYearly) {
        return {
          branch_id: this.model.branch_id,
          scoring_cycle: 'yearly',
          cycle: 'yearly',
          type: 'yearly',
          from_year: this.model.from_year,
          to_year: this.model.to_year,
          year: this.model.from_year
        }
      }
      const range = this.model.month_range || []
      return {
        branch_id: this.model.branch_id,
        scoring_cycle: 'month',
        cycle: 'month',
        type: 'month',
        from_month: range[0] ? moment(range[0]).format('YYYY-MM') : null,
        to_month: range[1] ? moment(range[1]).format('YYYY-MM') : null
      }
    },
    validate() {
      this.errors = new Errors()
      const errors = {}
      if (this.model.scoring_cycle === 'yearly') {
        if (!this.model.from_year) {
          errors.from_year = ['From year is required']
        }
        if (!this.model.to_year) {
          errors.to_year = ['To year is required']
        }
        if (
          this.model.from_year &&
          this.model.to_year &&
          this.model.from_year > this.model.to_year
        ) {
          errors.to_year = [
            'To year must be greater than or equal to from year'
          ]
        }
      } else {
        if (
          !this.model.month_range ||
          !this.model.month_range[0] ||
          !this.model.month_range[1]
        ) {
          errors.month_range = ['Month range is required']
        }
      }
      if (Object.keys(errors).length > 0) {
        this.errors = new Errors(errors)
        return false
      }
      return true
    },
    preview() {
      if (!this.validate()) return

      this.waiting = true
      this.src = ''
      const params = this.getFormattedParams()

      this.studentHeadcount(params)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(
            error.message || 'Report preview under development'
          )
          if (error.errors) {
            this.errors = new Errors(error.errors)
          }
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      if (!this.validate()) return

      this.exporting = true
      const params = this.getFormattedParams()

      this.studentHeadcountExcel(params)
        .then(response => {
          let filename =
            this.model.scoring_cycle === 'yearly'
              ? 'student-headcounts-yearly.xlsx'
              : 'student-headcounts.xlsx'
          if (response.headers && response.headers['content-disposition']) {
            filename = trim(
              response.headers['content-disposition']
                .substring(
                  response.headers['content-disposition'].indexOf('filename')
                )
                .replace('filename=', ''),
              '"'
            )
          }
          FileDownload(response.data, filename)
        })
        .catch(err => {
          if (err instanceof ArrayBuffer) {
            try {
              let error = JSON.parse(
                String.fromCharCode.apply(null, new Uint8Array(err))
              )
              this.$toasted.error(error.message)
            } catch (e) {
              this.$toasted.error('Error exporting Excel')
            }
          } else {
            this.$toasted.error(err.message || 'Export Excel under development')
          }
        })
        .finally(() => {
          this.exporting = false
        })
    }
  }
}
</script>
